import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "../api/client";
import type { ChannelKind, EnrollmentKeyCreated, TenantSummary } from "../api/types";
import { accessNotice } from "../components/account";
import { Empty, ErrorBox, Loading, relativeTime } from "../components/common";
import DesktopDownloads, { isOlderVersion } from "../components/desktopDownloads";
import { qaDesktop } from "../desktop";
import { useI18n } from "../i18n";

const EVENTS = [
  "run.finished",
  "run.failed",
  "run.passed",
  "device.needs_attention",
  "device.offline",
];

const CHANNEL_FIELDS: Record<ChannelKind, string[]> = {
  slack: ["webhook_url"],
  telegram: ["bot_token", "chat_id"],
  webhook: ["url"],
  email: ["host", "from", "to"],
};

export default function Settings() {
  const { t } = useI18n();
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });

  if (me.isLoading) return <Loading what={t("loading.settings")} />;
  if (me.error) return <ErrorBox error={me.error} />;

  return (
    <>
      <div className="page-header">
        <h2>{t("settings.title")}</h2>
      </div>

      <div className="col">
        <div className="card">
          <strong>{t("settings.signedIn")}</strong>
          <p style={{ margin: "8px 0 0" }}>{me.data!.email}</p>
          <p className="muted" style={{ margin: 0 }}>
            {t("settings.role", { role: me.data!.role })}
          </p>
        </div>

        <Plan tenant={me.data!.tenant} />
        <DesktopApp />
        <EnrollmentKeys />
        <ApiKeys />
        <Secrets />
        <Pools />
        <Channels />
      </div>
    </>
  );
}

// --- the account ------------------------------------------------------------

/**
 * Read-only on purpose: expiry and the device cap are ours to set (B2B §8), and
 * a customer seeing "0 of 10 used" answers most of the questions they would
 * otherwise write in about.
 */
function Plan({ tenant }: { tenant: TenantSummary }) {
  const { t } = useI18n();
  const devices = useQuery({ queryKey: ["devices"], queryFn: () => api.devices() });
  const notice = accessNotice(tenant, t);
  return (
    <div className="card">
      <strong>{t("account.plan")}</strong>
      <p style={{ margin: "8px 0 0" }}>{tenant.name}</p>
      <p className={notice.tone === "normal" ? "muted" : `${notice.tone}-text`} style={{ margin: 0 }}>
        {notice.text}
      </p>
      <p className="muted" style={{ margin: "8px 0 0" }}>
        {t("account.devicesUsed")}: {devices.data?.length ?? "—"}
        {" · "}
        {t("account.deviceCap")}: {tenant.max_devices ?? t("account.noCap")}
      </p>
    </div>
  );
}

// --- the desktop app --------------------------------------------------------

/**
 * Where the customer gets the app, and — when they are already reading this
 * inside it — whether their copy is the current one (DESKTOP §2.4).
 *
 * The buttons stay on screen in the app too: the computer being downloaded
 * *for* is often not the one doing the downloading.
 */
function DesktopApp() {
  const { t } = useI18n();
  // Read at render time rather than at import time: the bridge is injected by
  // the app's preload, and in a browser it is simply absent.
  const app = qaDesktop();
  const releases = useQuery({ queryKey: ["desktop-releases"], queryFn: api.desktopReleases });
  const release = releases.data?.customer ?? null;

  return (
    <div className="card">
      <strong>{t("settings.desktopApp")}</strong>
      <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
        {t("settings.desktopHint")}
      </p>

      {app && (
        <p className="muted" style={{ margin: "8px 0 0" }}>
          {t("settings.desktopRunning", { version: app.version })}
        </p>
      )}
      {app && release && isOlderVersion(app.version, release.version) && (
        <p style={{ margin: "4px 0 0" }}>
          {t("settings.desktopUpdate", { version: release.version })}
        </p>
      )}

      {releases.isLoading && <Loading what={t("loading.desktop")} />}
      {releases.error && <ErrorBox error={releases.error} />}
      {releases.data && !release && <Empty>{t("settings.desktopNone")}</Empty>}
      {release && <DesktopDownloads release={release} />}
    </div>
  );
}

// --- enrollment keys --------------------------------------------------------

/** The Gradle invocation that bakes a key (and the server it belongs to) into the agent APK. */
export function agentBuildCommand(serverUrl: string, key: string): string {
  return `cd agent-android && ./gradlew :app:assembleDebug -PagentServerUrl=${serverUrl} -PagentEnrollmentToken=${key}`;
}

function EnrollmentKeys() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [maxPhones, setMaxPhones] = useState("");
  const [minted, setMinted] = useState<EnrollmentKeyCreated | null>(null);
  const keys = useQuery({ queryKey: ["enrollment-keys"], queryFn: api.enrollmentKeys });

  const create = useMutation({
    mutationFn: () =>
      api.createEnrollmentKey({
        name: name.trim(),
        max_devices: maxPhones ? Number(maxPhones) : null,
      }),
    onSuccess: (key) => {
      setMinted(key);
      setName("");
      setMaxPhones("");
      queryClient.invalidateQueries({ queryKey: ["enrollment-keys"] });
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.revokeEnrollmentKey(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["enrollment-keys"] }),
  });

  return (
    <div className="card">
      <strong>{t("settings.enrollmentKeys")}</strong>
      <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
        {t("settings.enrollmentKeysHint")}
      </p>

      {minted && (
        <div className="card" style={{ background: "var(--bg)", margin: "8px 0" }}>
          <p className="muted" style={{ margin: 0, fontSize: 12 }}>
            {t("settings.mintedNote")}
          </p>
          <code className="mono" style={{ wordBreak: "break-all" }}>
            {minted.key}
          </code>
          <p className="muted" style={{ margin: "8px 0 4px", fontSize: 12 }}>
            {t("settings.enrollmentKeyBuild")}
          </p>
          <code className="mono" style={{ wordBreak: "break-all", display: "block" }}>
            {agentBuildCommand(minted.server_url, minted.key)}
          </code>
        </div>
      )}

      <div className="row" style={{ margin: "8px 0" }}>
        <input
          placeholder={t("settings.enrollmentKeyNamePlaceholder")}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <input
          type="number"
          min={1}
          placeholder={t("settings.enrollmentKeyCapPlaceholder")}
          value={maxPhones}
          onChange={(e) => setMaxPhones(e.target.value)}
          style={{ maxWidth: 180 }}
        />
        <button
          className="primary"
          disabled={!name.trim() || create.isPending}
          onClick={() => create.mutate()}
        >
          {t("settings.create")}
        </button>
      </div>
      {create.error && <ErrorBox error={create.error} />}
      {revoke.error && <ErrorBox error={revoke.error} />}

      <table>
        <thead>
          <tr>
            <th>{t("settings.th.name")}</th>
            <th>{t("settings.th.phones")}</th>
            <th>{t("settings.th.created")}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {(keys.data ?? []).map((key) => (
            <tr key={key.id}>
              <td>
                {key.name}{" "}
                {key.revoked_at && <span className="muted">{t("settings.revoked")}</span>}
                {/* Not a fault to fix: it explains why the Agent APK page
                    will not build with this one (B2B §16.7). */}
                {!key.revoked_at && !key.rebuildable && (
                  <span className="muted">{t("settings.keyLegacy")}</span>
                )}
              </td>
              <td>
                {key.max_devices === null
                  ? String(key.devices_enrolled)
                  : `${key.devices_enrolled} / ${key.max_devices}`}
              </td>
              <td className="muted">{relativeTime(key.created_at, t)}</td>
              <td>
                {!key.revoked_at && (
                  <button className="danger" onClick={() => revoke.mutate(key.id)}>
                    {t("settings.revoke")}
                  </button>
                )}
              </td>
            </tr>
          ))}
          {(keys.data ?? []).length === 0 && (
            <tr>
              <td colSpan={4} className="muted">
                {t("settings.noEnrollmentKeys")}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// --- API keys ---------------------------------------------------------------

function ApiKeys() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [minted, setMinted] = useState<string | null>(null);
  const keys = useQuery({ queryKey: ["api-keys"], queryFn: api.apiKeys });

  const create = useMutation({
    mutationFn: () => api.createApiKey(name),
    onSuccess: (key) => {
      setMinted(key.key);
      setName("");
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
    },
  });
  const revoke = useMutation({
    mutationFn: api.revokeApiKey,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["api-keys"] }),
  });

  return (
    <div className="card">
      <strong>{t("settings.apiKeys")}</strong>
      <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
        {t("settings.apiKeysHintA")} <code>qa</code> {t("settings.apiKeysHintB")}{" "}
        <code>QA_API_KEY</code> {t("settings.apiKeysHintC")}
      </p>

      {minted && (
        <div className="card" style={{ background: "var(--bg)", margin: "8px 0" }}>
          <p className="muted" style={{ margin: 0, fontSize: 12 }}>
            {t("settings.mintedNote")}
          </p>
          <code className="mono" style={{ wordBreak: "break-all" }}>
            {minted}
          </code>
        </div>
      )}

      <div className="row" style={{ margin: "8px 0" }}>
        <input
          placeholder={t("settings.keyNamePlaceholder")}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <button className="primary" disabled={!name || create.isPending} onClick={() => create.mutate()}>
          {t("settings.create")}
        </button>
      </div>
      {create.error && <ErrorBox error={create.error} />}

      <table>
        <thead>
          <tr>
            <th>{t("settings.th.name")}</th>
            <th>{t("settings.th.created")}</th>
            <th>{t("settings.th.lastUsed")}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {(keys.data ?? []).map((key) => (
            <tr key={key.id}>
              <td>
                {key.name} {key.revoked && <span className="muted">{t("settings.revoked")}</span>}
              </td>
              <td className="muted">{relativeTime(key.created_at, t)}</td>
              <td className="muted">
                {key.last_used_at ? relativeTime(key.last_used_at, t) : t("settings.never")}
              </td>
              <td>
                {!key.revoked && (
                  <button className="danger" onClick={() => revoke.mutate(key.id)}>
                    {t("settings.revoke")}
                  </button>
                )}
              </td>
            </tr>
          ))}
          {(keys.data ?? []).length === 0 && (
            <tr>
              <td colSpan={4} className="muted">
                {t("settings.noKeys")}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// --- secrets ----------------------------------------------------------------

function Secrets() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [key, setKey] = useState("");
  const [value, setValue] = useState("");
  const secrets = useQuery({ queryKey: ["secrets"], queryFn: api.secrets });

  const save = useMutation({
    mutationFn: () => api.setSecret(key, value),
    onSuccess: () => {
      setKey("");
      setValue("");
      queryClient.invalidateQueries({ queryKey: ["secrets"] });
    },
  });
  const remove = useMutation({
    mutationFn: api.deleteSecret,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["secrets"] }),
  });

  return (
    <div className="card">
      <strong>{t("settings.secrets")}</strong>
      <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
        {t("settings.secretsHintA")} <code>{"{{ secret.NAME }}"}</code>.{" "}
        {t("settings.secretsHintB")}
      </p>

      <div className="row" style={{ margin: "8px 0" }}>
        <input
          placeholder="QA_PASSWORD"
          value={key}
          onChange={(e) => setKey(e.target.value.toUpperCase())}
          style={{ maxWidth: 220 }}
        />
        <input
          type="password"
          placeholder={t("settings.valuePlaceholder")}
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <button
          className="primary"
          disabled={!/^[A-Z0-9_]+$/.test(key) || !value || save.isPending}
          onClick={() => save.mutate()}
        >
          {t("settings.save")}
        </button>
      </div>
      {save.error && <ErrorBox error={save.error} />}

      <ul style={{ paddingLeft: 18, margin: 0 }}>
        {(secrets.data ?? []).map((name) => (
          <li key={name} className="row" style={{ justifyContent: "space-between" }}>
            <span className="mono">{name}</span>
            <button className="danger" onClick={() => remove.mutate(name)}>
              {t("settings.delete")}
            </button>
          </li>
        ))}
        {(secrets.data ?? []).length === 0 && <li className="muted">{t("settings.noneStored")}</li>}
      </ul>
    </div>
  );
}

// --- pools ------------------------------------------------------------------

function Pools() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const pools = useQuery({ queryKey: ["pools"], queryFn: api.pools });
  const create = useMutation({
    mutationFn: () => api.createPool(name),
    onSuccess: () => {
      setName("");
      queryClient.invalidateQueries({ queryKey: ["pools"] });
    },
  });

  return (
    <div className="card">
      <strong>{t("settings.pools")}</strong>
      <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
        {t("settings.poolsHintA")} <code>{'{pool: "regression"}'}</code>.
      </p>
      <div className="row" style={{ margin: "8px 0" }}>
        <input placeholder="regression" value={name} onChange={(e) => setName(e.target.value)} />
        <button className="primary" disabled={!name || create.isPending} onClick={() => create.mutate()}>
          {t("settings.create")}
        </button>
      </div>
      {create.error && <ErrorBox error={create.error} />}
      <ul style={{ paddingLeft: 18, margin: 0 }}>
        {(pools.data ?? []).map((pool) => (
          <li key={pool.id}>{pool.name}</li>
        ))}
        {(pools.data ?? []).length === 0 && <li className="muted">{t("settings.noPools")}</li>}
      </ul>
    </div>
  );
}

// --- notification channels --------------------------------------------------

function Channels() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<ChannelKind>("slack");
  const [name, setName] = useState("");
  const [config, setConfig] = useState<Record<string, string>>({});
  const [tested, setTested] = useState<Record<string, string>>({});

  const channels = useQuery({ queryKey: ["channels"], queryFn: api.channels });
  const subscriptions = useQuery({ queryKey: ["subscriptions"], queryFn: api.subscriptions });

  const create = useMutation({
    mutationFn: () =>
      api.createChannel({
        name,
        kind,
        config: kind === "email" ? { ...config, to: (config.to ?? "").split(",") } : config,
      }),
    onSuccess: () => {
      setName("");
      setConfig({});
      queryClient.invalidateQueries({ queryKey: ["channels"] });
    },
  });
  const remove = useMutation({
    mutationFn: api.deleteChannel,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["channels"] }),
  });
  const subscribe = useMutation({
    mutationFn: (body: { channel_id: string; event: string }) => api.createSubscription(body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["subscriptions"] }),
  });
  const unsubscribe = useMutation({
    mutationFn: api.deleteSubscription,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["subscriptions"] }),
  });

  return (
    <div className="card">
      <strong>{t("settings.channels")}</strong>
      <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
        {t("settings.channelsHint")}
      </p>

      <div className="row" style={{ margin: "8px 0", flexWrap: "wrap" }}>
        <select
          value={kind}
          onChange={(e) => {
            setKind(e.target.value as ChannelKind);
            setConfig({});
          }}
          style={{ maxWidth: 140 }}
        >
          {(Object.keys(CHANNEL_FIELDS) as ChannelKind[]).map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>
        <input
          placeholder={t("settings.channelNamePlaceholder")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{ maxWidth: 180 }}
        />
        {CHANNEL_FIELDS[kind].map((field) => (
          <input
            key={field}
            placeholder={field}
            value={config[field] ?? ""}
            onChange={(e) => setConfig({ ...config, [field]: e.target.value })}
            style={{ maxWidth: 220 }}
          />
        ))}
        <button
          className="primary"
          disabled={!name || CHANNEL_FIELDS[kind].some((f) => !config[f]) || create.isPending}
          onClick={() => create.mutate()}
        >
          {t("settings.add")}
        </button>
      </div>
      {create.error && <ErrorBox error={create.error} />}

      {(channels.data ?? []).map((channel) => {
        const subs = (subscriptions.data ?? []).filter((s) => s.channel_id === channel.id);
        return (
          <div key={channel.id} className="card" style={{ background: "var(--bg)", marginTop: 8 }}>
            <div className="row" style={{ justifyContent: "space-between" }}>
              <span>
                <strong>{channel.name}</strong> <span className="muted">· {channel.kind}</span>
                {!channel.enabled && <span className="muted">{t("settings.disabled")}</span>}
              </span>
              <span className="row">
                {tested[channel.id] && <span className="muted">{tested[channel.id]}</span>}
                <button
                  onClick={async () => {
                    const result = await api.testChannel(channel.id);
                    setTested({
                      ...tested,
                      [channel.id]: result.ok
                        ? t("settings.delivered")
                        : t("settings.failed", { error: result.error ?? "" }),
                    });
                  }}
                >
                  {t("settings.sendTest")}
                </button>
                <button className="danger" onClick={() => remove.mutate(channel.id)}>
                  {t("settings.delete")}
                </button>
              </span>
            </div>

            <div className="row" style={{ marginTop: 8, flexWrap: "wrap" }}>
              {subs.map((sub) => (
                <button
                  key={sub.id}
                  onClick={() => unsubscribe.mutate(sub.id)}
                  title={t("settings.removeTitle")}
                >
                  {sub.event} ×
                </button>
              ))}
              <select
                value=""
                onChange={(e) =>
                  e.target.value &&
                  subscribe.mutate({ channel_id: channel.id, event: e.target.value })
                }
                style={{ maxWidth: 200 }}
              >
                <option value="">{t("settings.subscribePlaceholder")}</option>
                {EVENTS.filter((event) => !subs.some((s) => s.event === event)).map((event) => (
                  <option key={event} value={event}>
                    {event}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );
      })}
      {(channels.data ?? []).length === 0 && (
        <p className="muted">{t("settings.noChannels")}</p>
      )}
    </div>
  );
}
